import { Validation } from './Validation';
import { ValidationSchema } from './interfaces';
import FileNameValidationSchema from './FileNameValidationSchema';
import * as Yup from 'yup';

export class MultipleFilesValidationSchema implements ValidationSchema {
  public name = '';
  public label = '';
  public dependsOn: string[] = [];
  public type = 'array';
  public required = {
    message: '',
    value: false,
  };
  public nullable = true;
  public min = {
    message: '',
    value: 1,
  };

  constructor(label: string, required?: boolean, minFiles?: number) {
    this.label = label;
    if (minFiles !== undefined) {
      this.min = {
        message: `At least ${minFiles} file${
          minFiles > 1 ? 's are' : ' is'
        } required`,
        value: minFiles,
      };
    }
    if (required)
      this.required = {
        message: 'This document is required.',
        value: true,
      };
  }

  public buildShape(schema) {
    const { label, required, min } = this;
    return Yup.array()
      .transform((originalValue) => this.cleanupFileArray(originalValue))
      .of(FileNameValidationSchema(label, required.value))
      .test('validate-min-files', this.required.message, (arr) => {
        const cleanedArray = this.cleanupFileArray(arr);
        // Only enforce minimum length if the field is required
        if (required.value) {
          return cleanedArray.length >= min.value;
        }
        return true;
      });
  }

  /**
   * Removes empty placeholder items from the end of the file array
   *
   * The MultipleRequiredDocumentUpload component adds an empty item at the end
   * of the array to allow users to add more documents. This function removes
   * that item to prevent validation errors.
   *
   * @param value - The array to clean up
   * @returns The array with empty placeholders removed
   */
  private cleanupFileArray(value) {
    if (!Array.isArray(value)) {
      return value;
    }
    return value.filter((item, index) => {
      const isLastItem = index === value.length - 1;
      // Don't filter out items that aren't the last one
      if (!isLastItem) {
        return true;
      }
      // Check if the last item is an empty placeholder
      const isUndefined = item === undefined;
      const isEmptyObject = item && Object.keys(item).length === 0;
      const isEmptyFilesArray =
        item &&
        typeof item === 'object' &&
        Array.isArray(item.files) &&
        item.files.length === 0;
      // Keep the item only if it's NOT an empty placeholder
      return !(isUndefined || isEmptyObject || isEmptyFilesArray);
    });
  }
}

export default (label: string, required?: boolean, minFiles?: number) =>
  new Validation(
    new MultipleFilesValidationSchema(label, required, minFiles)
  ).buildSchema();
